import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import MoneyProgressStrip from "@/components/MoneyProgressStrip";
import {
  Landmark, CreditCard, Copy, Check, AlertTriangle,
  Wallet, PiggyBank, Receipt, Smartphone, MapPin, ArrowRight,
  ShieldCheck, ShieldOff, Eye, EyeOff, X, Package, Truck, Plus, Lock, Unlock, Ban } from "lucide-react";
import DepositModal from "@/components/DepositModal";
import AddMoneyModal from "@/components/AddMoneyModal";
import WalletPanel from "@/components/WalletPanel";

const ACCOUNT_META = {
  spending: { label: "Spending", icon: Wallet,
    blurb: "Day-to-day money. Has its own debit card." },
};

const CARD_KEYS = ["spending"];

export default function Banking() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [profileForm, setProfileForm] = useState({});
  const [profileSavedAt, setProfileSavedAt] = useState(null);
  const [revealOpen, setRevealOpen] = useState(null); // card_key or null
  const [revealedDetails, setRevealedDetails] = useState(null);
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealError, setRevealError] = useState("");
  const [showPan, setShowPan] = useState(false);
  const [depositOpen, setDepositOpen] = useState(() => {
    // Auto-open when returning from Stripe checkout (success or cancel)
    if (typeof window === "undefined") return false;
    const sp = new URLSearchParams(window.location.search);
    return sp.has("stripe_session_id") || sp.has("stripe_cancelled");
  });
  const [addMoneyOpen, setAddMoneyOpen] = useState(false);

  const load = async () => {
    setError("");
    try {
      const r = await api.get("/banking");
      setData(r.data);
      setProfileForm({ ...(r.data.profile || {}) });
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
      setData({ accounts: {}, cards: {}, card_rules: {}, profile: {}, enabled: false, env: null });
    }
  };
  useEffect(() => { load(); }, []);

  const reprovision = async () => {
    setBusy(true); setError("");
    try { await api.post("/banking/provision"); await load(); }
    catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const saveProfile = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/banking/profile", profileForm);
      setProfileSavedAt(new Date().toISOString());
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };




  const toggleLock = async (cardKey, locked) => {
    setBusy(true); setError("");
    try { await api.post(`/banking/cards/${cardKey}/lock`, { locked }); await load(); }
    catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const [replaceFor, setReplaceFor] = useState(null);
  const [replaceReason, setReplaceReason] = useState("lost");
  const [replaceMsg, setReplaceMsg] = useState("");
  const replaceCard = async () => {
    setBusy(true); setError(""); setReplaceMsg("");
    try {
      const { data } = await api.post(`/banking/cards/${replaceFor}/replace`, { reason: replaceReason });
      setReplaceMsg(`Replacement ordered — $${data.fee.toFixed(2)} charged to Spending. Your new card is on its way.`);
      setReplaceFor(null); await load();
    } catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const orderPhysical = async (cardKey) => {
    setBusy(true); setError("");
    try {
      await api.post(`/banking/cards/${cardKey}/order-physical`);
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const openReveal = async (cardKey) => {
    setRevealOpen(cardKey); setRevealedDetails(null);
    setRevealError(""); setShowPan(false); setRevealBusy(true);
    try {
      const r = await api.post(`/banking/cards/${cardKey}/reveal`);
      setRevealedDetails(r.data);
    } catch (e) {
      setRevealError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRevealBusy(false); }
  };
  const closeReveal = () => {
    setRevealOpen(null); setRevealedDetails(null);
    setShowPan(false); setRevealError("");
  };
  const formatPan = (pan) => (pan || "").replace(/(.{4})/g, "$1 ").trim();

  const copy = (txt, key) => {
    navigator.clipboard.writeText(txt);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (!data) return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-6 py-20 font-mono text-moss">loading…</div>
    </div>
  );

  const accounts = data.accounts || {};
  const cards = data.cards || {};
  const isProd = data.env === "production";
  const accountKeys = ["spending"];
  const provisioned = accountKeys.every(k => accounts[k]?.id)
                   && CARD_KEYS.every(k => cards[k]?.id);

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Banking</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">One account. One card.</h1>
        <p className="text-moss mt-3 max-w-2xl leading-relaxed">
          <strong>Spending</strong> is your day-to-day account and comes with its own debit card.
        </p>

        <MoneyProgressStrip />

        {isProd && (
          <div className="mt-6 flex items-start gap-3 bg-clay-light border border-clay/30 rounded-lg p-4" data-testid="banking-prod-warning">
            <AlertTriangle className="w-5 h-5 text-clay shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="text-sm text-ink">
              <strong>Production:</strong> these are real bank accounts and real plastic cards
              at our bank partner. Cards issue after KYB review.
            </div>
          </div>
        )}

        {error && <div className="mt-6 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="banking-error">{error}</div>}

        {!provisioned && data.enabled && (
          <div className="mt-6 flex items-center justify-between bg-linen border border-sand rounded-lg p-4">
            <div className="text-sm text-moss">Some accounts or cards are still being provisioned.</div>
            <button onClick={reprovision} disabled={busy}
              className="bg-forest text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-forest-deep disabled:opacity-60"
              data-testid="reprovision-btn">
              {busy ? "Working…" : "Retry provisioning"}
            </button>
          </div>
        )}

        {/* Three account cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-10" data-testid="banking-accounts-grid">
          {accountKeys.map(key => {
            const acc = accounts[key];
            const meta = ACCOUNT_META[key];
            const Icon = meta.icon;
            return (
              <div key={key} className="bg-linen border border-sand rounded-2xl p-6" data-testid={`account-${key}`}>
                <Icon className="w-7 h-7 text-forest" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl mt-4">{meta.label}</h3>
                <p className="text-xs text-sage mt-2 leading-relaxed">{meta.blurb}</p>

                {!acc && (
                  <div className="mt-5 text-sm text-sage italic">Not provisioned yet.</div>
                )}

                {acc && (
                  <div className="mt-5 space-y-3 text-sm">
                    <div className="bg-ink text-cream rounded-lg p-3" data-testid={`account-${key}-balance`}>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Available balance</div>
                      <div className="font-mono text-2xl mt-0.5">
                        {acc.available_balance != null
                          ? `$${acc.available_balance.toFixed(2)}`
                          : <span className="text-sage text-sm">—</span>}
                      </div>
                      {acc.current_balance != null && acc.current_balance !== acc.available_balance && (
                        <div className="text-[11px] text-sage-mid mt-1">Pending settled: ${acc.current_balance.toFixed(2)}</div>
                      )}
                    </div>
                    {key === "spending" && (
                      <>
                        <button onClick={() => setAddMoneyOpen(true)}
                          className="w-full inline-flex items-center justify-center gap-1.5 bg-forest text-white rounded-lg px-4 py-2 text-xs font-medium hover:bg-forest-deep"
                          data-testid="add-money-btn">
                          <Plus className="w-3.5 h-3.5" /> Add money
                        </button>
                        <p className="text-[10px] text-sage text-center leading-relaxed -mt-1 inline-flex items-center justify-center gap-1" data-testid="no-transfers-note">
                          <Ban className="w-3 h-3" /> Card only · no transfers to other accounts or people, no cash withdrawals
                        </p>
                        <a href="/activity"
                          className="w-full inline-flex items-center justify-center gap-1.5 bg-ink text-white rounded-lg px-4 py-2 text-xs font-medium hover:bg-ink-deep"
                          data-testid="transactions-btn">
                          <Receipt className="w-3.5 h-3.5" /> Transactions
                        </a>
                        <a href="/statements"
                          className="w-full inline-flex items-center justify-center gap-1.5 bg-white border border-sand text-ink rounded-lg px-4 py-2 text-xs font-medium hover:bg-cream-warm"
                          data-testid="statements-btn">
                          <Receipt className="w-3.5 h-3.5" /> Tax statements
                        </a>
                      </>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Status</div>
                      <span className={`inline-flex mt-1 items-center px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wider ${
                        acc.status === "open" ? "bg-forest text-white" : "bg-sand-light text-moss"
                      }`} data-testid={`account-${key}-status`}>{acc.status}</span>
                    </div>
                    {acc.routing_number && (
                      <>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Routing #</div>
                          <button onClick={() => copy(acc.routing_number, `${key}-rt`)}
                            className="font-mono text-xs text-ink mt-1 inline-flex items-center gap-1.5 hover:text-forest"
                            data-testid={`copy-${key}-routing`}>
                            {acc.routing_number}
                            {copied === `${key}-rt` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Account #</div>
                          <button onClick={() => copy(acc.account_number, `${key}-an`)}
                            className="font-mono text-xs text-ink mt-1 inline-flex items-center gap-1.5 hover:text-forest"
                            data-testid={`copy-${key}-account`}>
                            {acc.account_number}
                            {copied === `${key}-an` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Debit card */}
        <h2 className="font-serif text-3xl mt-16">Your card</h2>
        <div className="mt-3 inline-flex items-start gap-2 bg-clay-light border border-clay/30 rounded-lg px-3 py-2 text-xs text-ink max-w-2xl" data-testid="card-only-notice">
          <AlertTriangle className="w-3.5 h-3.5 text-clay shrink-0 mt-0.5" />
          <span><strong>Card purchases only.</strong> This card can&apos;t send money to other accounts or people, and cash withdrawals aren&apos;t possible — period. Money leaves Spending only when you tap or swipe.</span>
        </div>
        {replaceMsg && <div className="mt-3 text-xs text-forest bg-[#E8EFE9] border border-forest/30 rounded-lg px-3 py-2" data-testid="replace-card-msg">{replaceMsg}</div>}

        <div className="grid md:grid-cols-2 gap-6 mt-6" data-testid="cards-grid">
          {CARD_KEYS.map(key => {
            const card = cards[key];
            const acc = accounts[key];
            return (
              <div key={key} className="bg-linen border border-sand rounded-2xl p-7" data-testid={`card-${key}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">{ACCOUNT_META[key].label} card</div>
                    <h3 className="font-serif text-xl mt-1">{ACCOUNT_META[key].label} card</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {card?.locked && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-clay text-white text-[9px] font-semibold uppercase tracking-wider" data-testid={`card-locked-pill-${key}`}><Lock className="w-2.5 h-2.5" /> Locked</span>}
                    <CreditCard className="w-5 h-5 text-forest" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => toggleLock(key, !card?.locked)} disabled={busy}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-60 ${card?.locked ? "bg-forest text-white hover:bg-forest-deep" : "border border-clay text-clay hover:bg-clay hover:text-white"}`}
                    data-testid={`card-lock-toggle-${key}`}>
                    {card?.locked ? <><Unlock className="w-3.5 h-3.5" /> Unlock card</> : <><Lock className="w-3.5 h-3.5" /> Lock card</>}
                  </button>
                  <button onClick={() => { setReplaceFor(key); setReplaceReason("lost"); }} disabled={busy}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium border border-sand text-ink hover:bg-sand-light transition-colors disabled:opacity-60"
                    data-testid={`card-replace-btn-${key}`}>
                    <AlertTriangle className="w-3.5 h-3.5 text-gold" /> Lost or stolen? Replace · $5
                  </button>
                </div>

                {!card && (
                  <div className="mt-6 text-sm text-moss">
                    Card not yet issued. Once our bank partner completes KYB review, it appears here.
                    <button onClick={reprovision} disabled={busy}
                      className="mt-4 w-full bg-forest text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-forest-deep disabled:opacity-60"
                      data-testid={`card-${key}-retry-btn`}>
                      {busy ? "Checking…" : "Check again"}
                    </button>
                  </div>
                )}

                {card && (
                  <>
                    <div className="bg-ink text-cream rounded-xl p-5 mt-5" data-testid={`card-${key}-visual`}>
                      <div className="flex items-start justify-between">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Debit · {ACCOUNT_META[key].label}</div>
                        <CreditCard className="w-4 h-4 opacity-60" strokeWidth={1.5} />
                      </div>
                      <div className="font-mono text-xl mt-8 tracking-widest">•••• •••• •••• {card.last4 || "0000"}</div>
                      <div className="flex items-end justify-between mt-4">
                        <div className="text-sm">{card.description}</div>
                        <div className="font-mono text-sm">
                          {card.expiration_month?.toString().padStart(2,"0") || "--"}/{(card.expiration_year || "----").toString().slice(-2)}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => openReveal(key)}
                      data-testid={`reveal-card-${key}-btn`}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-cream border border-sand text-ink rounded-lg px-4 py-2 text-xs font-medium hover:border-forest hover:text-forest transition-colors">
                      <Eye className="w-3.5 h-3.5" /> Show card number & CVV
                    </button>

                    <WalletPanel cardKey={key} card={card} onEnabled={load} />
                    {/* Physical card / shipping tracker */}
                    {card.physical_card ? (
                      <div className="mt-3 bg-cream border border-sand rounded-lg p-3" data-testid={`shipping-${key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-sage">
                            <Truck className="w-3.5 h-3.5" /> Plastic card
                          </div>
                          <ShipmentBadge status={card.physical_card.shipment_status} />
                        </div>
                        {card.physical_card.replacement_reason && (
                          <div className="mt-1.5 text-[10px] text-gold inline-flex items-center gap-1" data-testid={`replacement-label-${key}`}>
                            <AlertTriangle className="w-3 h-3" /> Replacement card ({card.physical_card.replacement_reason}) · previous card canceled
                          </div>
                        )}
                        <div className="mt-2 text-[11px] text-moss space-y-0.5">
                          <div>Method: <span className="text-ink">{card.physical_card.method?.toUpperCase() || "—"}</span></div>
                          {card.physical_card.carrier && (
                            <div>Carrier: <span className="text-ink">{card.physical_card.carrier}</span></div>
                          )}
                          {card.physical_card.tracking_number && (
                            <div>Tracking: <span className="font-mono text-ink">{card.physical_card.tracking_number}</span></div>
                          )}
                          {card.physical_card.shipped_at && (
                            <div>Shipped: <span className="font-mono text-ink">{new Date(card.physical_card.shipped_at).toLocaleDateString()}</span></div>
                          )}
                          {card.physical_card.estimated_delivery_at && (
                            <div>Est. delivery: <span className="font-mono text-ink">{new Date(card.physical_card.estimated_delivery_at).toLocaleDateString()}</span></div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => orderPhysical(key)} disabled={busy}
                        data-testid={`order-physical-${key}-btn`}
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-cream border border-dashed border-forest text-forest rounded-lg px-4 py-2 text-xs font-medium hover:bg-forest hover:text-white transition-colors disabled:opacity-60">
                        <Package className="w-3.5 h-3.5" /> {busy ? "Ordering…" : "Order physical card"}
                      </button>
                    )}

                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Apple Pay/Ship */}
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className="bg-linen border border-sand rounded-2xl p-7" data-testid="banking-profile-card">
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-forest" strokeWidth={1.5} />
              <MapPin className="w-5 h-5 text-forest" strokeWidth={1.5} />
              <h3 className="font-serif text-xl">Apple Pay & shipping</h3>
            </div>
            <p className="text-xs text-sage mt-2">
              Used to provision Apple Pay and to mail your physical cards.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label="Apple Pay phone">
                <input type="tel" value={profileForm.apple_pay_phone || ""}
                  onChange={e => setProfileForm({ ...profileForm, apple_pay_phone: e.target.value })}
                  className="w-full bg-cream border border-sand rounded-md px-3 py-2 text-sm"
                  data-testid="apple-pay-phone-input" />
              </Field>
              <Field label="Apple ID email">
                <input type="email" value={profileForm.apple_pay_email || ""}
                  onChange={e => setProfileForm({ ...profileForm, apple_pay_email: e.target.value })}
                  className="w-full bg-cream border border-sand rounded-md px-3 py-2 text-sm"
                  data-testid="apple-pay-email-input" />
              </Field>
              <div className="col-span-2"><Field label="Ship to">
                <input value={profileForm.ship_name || ""}
                  onChange={e => setProfileForm({ ...profileForm, ship_name: e.target.value })}
                  placeholder="Recipient name"
                  className="w-full bg-cream border border-sand rounded-md px-3 py-2 text-sm mb-2"
                  data-testid="ship-name-input" />
                <input value={profileForm.ship_address1 || ""}
                  onChange={e => setProfileForm({ ...profileForm, ship_address1: e.target.value })}
                  placeholder="Street address"
                  className="w-full bg-cream border border-sand rounded-md px-3 py-2 text-sm mb-2"
                  data-testid="ship-address1-input" />
                <input value={profileForm.ship_address2 || ""}
                  onChange={e => setProfileForm({ ...profileForm, ship_address2: e.target.value })}
                  placeholder="Apt / suite"
                  className="w-full bg-cream border border-sand rounded-md px-3 py-2 text-sm mb-2"
                  data-testid="ship-address2-input" />
                <div className="grid grid-cols-3 gap-2">
                  <input value={profileForm.ship_city || ""}
                    onChange={e => setProfileForm({ ...profileForm, ship_city: e.target.value })}
                    placeholder="City"
                    className="bg-cream border border-sand rounded-md px-3 py-2 text-sm"
                    data-testid="ship-city-input" />
                  <input value={profileForm.ship_state || ""}
                    onChange={e => setProfileForm({ ...profileForm, ship_state: e.target.value })}
                    placeholder="State"
                    className="bg-cream border border-sand rounded-md px-3 py-2 text-sm"
                    data-testid="ship-state-input" />
                  <input value={profileForm.ship_postal || ""}
                    onChange={e => setProfileForm({ ...profileForm, ship_postal: e.target.value })}
                    placeholder="ZIP"
                    className="bg-cream border border-sand rounded-md px-3 py-2 text-sm"
                    data-testid="ship-postal-input" />
                </div>
              </Field></div>
            </div>
            <div className="mt-5 flex items-center justify-between">
              <p className="text-[11px] text-sage">
                {profileSavedAt ? "Saved." : "Saves Apple Pay contact + card shipping address."}
              </p>
              <button onClick={saveProfile} disabled={busy}
                className="bg-forest text-white rounded-lg px-4 py-1.5 text-xs font-medium hover:bg-forest-deep disabled:opacity-60"
                data-testid="save-banking-profile-btn">
                {busy ? "Saving…" : "Save details"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Deposit from debit card (legacy Stripe flow — kept for return-from-Stripe redirect handling) */}
      <DepositModal open={depositOpen}
                    onClose={() => { setDepositOpen(false); load(); }}
                    onSuccess={() => load()} />

      {/* Add money — preferred flow */}
      <AddMoneyModal open={addMoneyOpen}
                     onClose={() => setAddMoneyOpen(false)}
                     banking={data}
                     userName={user?.name || user?.email || ""} />

      {/* Card reveal modal */}
      {revealOpen && (
        <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="card-reveal-modal">
          <div className="bg-cream rounded-2xl border border-sand p-7 max-w-md w-full">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">{ACCOUNT_META[revealOpen].label} debit · digital card</div>
                <h3 className="font-serif text-2xl mt-1">Card details</h3>
              </div>
              <button onClick={closeReveal} className="text-sage hover:text-ink" data-testid="card-reveal-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {revealBusy && (
              <div className="mt-6 text-sm text-sage">Fetching card details…</div>
            )}
            {revealError && (
              <div className="mt-6 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="card-reveal-error">{revealError}</div>
            )}

            {revealedDetails && (
              <div className="mt-6 space-y-4" data-testid="card-reveal-details">
                <div className="bg-ink text-cream rounded-xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Card number</div>
                  <div className="font-mono text-lg sm:text-xl tracking-widest mt-2 select-all" data-testid="card-pan">
                    {showPan ? formatPan(revealedDetails.primary_account_number) : "•••• •••• •••• " + (revealedDetails.primary_account_number || "").slice(-4)}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Expiry</div>
                      <div className="font-mono text-sm mt-1" data-testid="card-expiry">
                        {String(revealedDetails.expiration_month || "").padStart(2, "0")} / {(revealedDetails.expiration_year || "").toString().slice(-2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">CVV</div>
                      <div className="font-mono text-sm mt-1 select-all" data-testid="card-cvv">
                        {showPan ? revealedDetails.verification_code : "•••"}
                      </div>
                    </div>
                  </div>
                </div>

                <button onClick={() => setShowPan(v => !v)}
                  data-testid="toggle-pan-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white border border-sand rounded-lg px-4 py-2.5 text-sm font-medium text-ink hover:border-forest transition-colors">
                  {showPan ? <><EyeOff className="w-4 h-4" /> Hide number & CVV</> : <><Eye className="w-4 h-4" /> Reveal full number & CVV</>}
                </button>

                <button onClick={() => {
                  navigator.clipboard.writeText(revealedDetails.primary_account_number || "");
                  setCopied("pan"); setTimeout(() => setCopied(""), 1500);
                }}
                  data-testid="copy-pan-btn"
                  className="w-full inline-flex items-center justify-center gap-2 text-sm text-moss hover:text-forest py-2">
                  {copied === "pan" ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy card number</>}
                </button>

                <p className="text-[11px] text-sage leading-relaxed">
                  These details are not stored anywhere in Withdraw Guard. They&apos;re fetched from our bank partner on-demand and disappear when you close this dialog. Don&apos;t share with anyone.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {replaceFor && (
        <div className="fixed inset-0 bg-ink/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="replace-card-dialog">
          <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">Replacement card</div>
            <h3 className="font-serif text-3xl mt-3">Replace your {replaceFor} card</h3>
            <p className="text-sm text-moss mt-3 leading-relaxed">
              We&apos;ll cancel the current plastic with our bank partner and mail a new card to your saved shipping address.
              A <strong className="text-ink">$5.00 fee</strong> is taken from your Spending account only — if there aren&apos;t enough funds, nothing is ordered.
            </p>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-sage mt-5">Reason</label>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="replace-reason">
              {["lost", "stolen", "damaged"].map((r) => (
                <button key={r} type="button" onClick={() => setReplaceReason(r)}
                  className={`rounded-lg border px-3 py-2 text-sm capitalize ${replaceReason === r ? "bg-ink text-white border-ink" : "border-sand hover:border-forest"}`}
                  data-testid={`replace-reason-${r}`}>{r}</button>
              ))}
            </div>
            {error && <div className="text-sm text-clay mt-3" data-testid="replace-card-error">{error}</div>}
            <div className="mt-5 flex gap-2">
              <button onClick={replaceCard} disabled={busy} className="flex-1 bg-forest text-white rounded-lg px-6 py-3 font-medium hover:bg-forest-deep disabled:opacity-50" data-testid="replace-card-submit">
                {busy ? "Ordering…" : "Order replacement · $5.00"}
              </button>
              <button onClick={() => setReplaceFor(null)} className="border border-sand rounded-lg px-5 py-3 text-sm" data-testid="replace-card-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ShipmentBadge({ status }) {
  const s = (status || "").toLowerCase();
  const palette =
    s === "delivered"
      ? "bg-forest text-white border-forest"
      : s === "shipped" || s === "in_transit"
      ? "bg-mint text-forest-deep border-forest/40"
      : s === "returned" || s === "failed" || s === "rejected"
      ? "bg-clay-light text-clay border-clay/40"
      : "bg-cream text-moss border-sand";
  const label = s ? s.replace(/_/g, " ") : "pending";
  return (
    <span
      data-testid="shipment-badge"
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold border ${palette}`}
    >
      {label}
    </span>
  );
}
